import {Injectable} from '@angular/core';
import {Logger} from '../../../utils/Logger';
import {Toaster, Type} from '../../../toaster/Toaster';
import {ConfigService} from '../../../service/config.service';
import {PortfolioService} from '../../../service/portfolio.service';
import {Keys, StorageService} from '../../../service/storage.service';
import {LenderDetailsService} from '../../../service/lender-details.service';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree} from '@angular/router';

@Injectable()
export class AutoInvestGuard implements CanActivate {
    private readonly router: Router;
    private readonly configService: ConfigService;
    private readonly storageService: StorageService;
    private readonly portfolioService: PortfolioService;
    private readonly lenderDetailsService: LenderDetailsService;

    constructor(router: Router, configService: ConfigService, storageService: StorageService,
                lenderDetailsService: LenderDetailsService, portfolioService: PortfolioService) {
        this.router = router;
        this.configService = configService;
        this.storageService = storageService;
        this.lenderDetailsService = lenderDetailsService;
        this.portfolioService = portfolioService;
    }

    async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
        const tempPortfolioId = route.params.tempPortfolioId;
        const {portfolioAmount} = await this.portfolioService.getLocalPortfolioCreationData(tempPortfolioId);
        const portfolioAmountCheck = async () => portfolioAmount >= this.configService.getMinimumInvestmentLimit();
        const lenderRegulatoryCheck = async () => await this.storageService.getObjectFromSession<boolean>(Keys.LENDER_10LAKH_LIMIT_CHECK);
        const loanAgreementReviewedCheck = async () => await this.storageService.getObjectFromSession<boolean>(Keys.LENDER_LOAN_AGREEMENT_REVIEWED);
        const walletBalanceCheck = async () => {
            const {lender_info: lender} = await this.lenderDetailsService.getLenderDetails(this.storageService.getItem(Keys.LENDER_ID));
            return lender.wallet.amount >= portfolioAmount;
        };

        const checkResults = await Promise.all([portfolioAmountCheck(), lenderRegulatoryCheck(),
            loanAgreementReviewedCheck(), walletBalanceCheck()]);
        if (checkResults.every(v => v)) {
            return true;
        } else {
            Toaster.toast(`One of the pre-requisites for portfolio creation was not satisfied. Please try again.`, Type.Error);
            Logger.log(`One of the checks for auto-investment failed. Redirecting to dashboard.`);
            return this.dashboardUrlTree();
        }
    }

    private dashboardUrlTree() {
        return this.router.createUrlTree(['/investordashboard']);
    }
}